import React, { useEffect, useRef, useState } from 'react';
import { 
  RefreshCw, ExternalLink, BarChart2, Inbox, 
  History, Clock, CheckCircle, ArrowRight, Type, Plus, Minus, Maximize2 
} from 'lucide-react';
import { embedDashboard } from "@superset-ui/embedded-sdk";
import { useTranslation } from 'react-i18next'; // 🎯 استدعاء الترجمة
import api from './axiosConfig';

// --- مكون الـ Workflow Inbox الجديد ---
const WorkflowInbox = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar' || i18n.language === 'ur';
  const [activeTasks, setActiveTasks] = useState([]);
  const [historyTasks, setHistoryTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subTab, setSubTab] = useState('active'); 
  const [skip, setSkip] = useState(0);
  const [total, setTotal] = useState(0);
  const limit = 10;

  const fetchNotifications = async () => {
      try {
          setLoading(true);
          const res = await api.get(`/notifications/all?skip=${skip}&limit=${limit}`);
          const allData = res.data.data;
          setTotal(res.data.total);
          setActiveTasks(allData.filter(n => n.is_active));
          setHistoryTasks(allData.filter(n => !n.is_active));
      } catch (err) {
          console.error("Error fetching notifications", err);
      } finally {
          setLoading(false);
      }
  };

  useEffect(() => { fetchNotifications(); }, [skip]);

  const PaginationControls = () => (
      <div className="flex justify-center items-center gap-4 mt-4 py-2 border-t border-gray-100 bg-gray-50/50">
          <button 
              disabled={skip === 0}
              onClick={() => setSkip(prev => Math.max(0, prev - limit))}
              className="p-1 px-3 bg-white border rounded text-[10px] font-bold disabled:opacity-30 hover:bg-gray-100"
          >
              {t('dashboard.previous')}
          </button>
          <span className="text-[10px] font-black text-[#005495]">
              {t('dashboard.showing')} {skip + 1} - {Math.min(skip + limit, total)} {t('dashboard.of')} {total}
          </span>
          <button 
              disabled={skip + limit >= total}
              onClick={() => setSkip(prev => prev + limit)}
              className="p-1 px-3 bg-white border rounded text-[10px] font-bold disabled:opacity-30 hover:bg-gray-100"
          >
              {t('dashboard.next')}
          </button>
      </div>
  );

  const handleAction = (link) => {
    window.location.href = link;
  };

  return (
    <div className={`bg-white border border-gray-300 rounded shadow-sm mt-6 overflow-hidden min-h-[500px] ${i18n.language === 'ar' || i18n.language === 'ur' ? 'text-right' : 'text-left'}`}>
      <div className="bg-[#5c85b5] text-white px-3 py-1 flex justify-between items-center text-[11px] font-bold">
        <div className={`flex items-center gap-2 `}>
          <Inbox size={14} />
          <span>{t('dashboard.workflow_center')}</span>
        </div>
        <RefreshCw size={12} className="cursor-pointer" onClick={fetchNotifications} />
      </div>

      <div className="flex border-b border-gray-200 bg-gray-50">
        <button 
          onClick={() => setSubTab('active')}
          className={`px-6 py-2 text-[11px] font-black uppercase transition-all ${subTab === 'active' ? 'bg-white border-r border-gray-200 text-[#005495] shadow-sm' : 'text-gray-400 hover:text-gray-600'}`}
        >
          {t('dashboard.active_tasks')} ({activeTasks.length})
        </button>
        <button 
          onClick={() => setSubTab('history')}
          className={`px-6 py-2 text-[11px] font-black uppercase transition-all ${subTab === 'history' ? 'bg-white border-l border-gray-200 text-[#005495] shadow-sm' : 'text-gray-400 hover:text-gray-600'}`}
        >
          {t('dashboard.history_tasks')} ({historyTasks.length})
        </button>
      </div>

      <div className="p-4">
        {loading ? (
          <div className="flex justify-center p-10"><RefreshCw className="animate-spin text-blue-500" /></div>
        ) : (
          <div className="divide-y divide-gray-100">
            {(subTab === 'active' ? activeTasks : historyTasks).length === 0 ? (
              <div className="text-center py-10 opacity-30 flex flex-col items-center">
                <Inbox size={40} />
                <span className="text-[12px] font-bold uppercase mt-2">{t('dashboard.no_data')}</span>
              </div>
            ) : (
              (subTab === 'active' ? activeTasks : historyTasks).map((task, idx) => (
                <div key={idx} className={`py-3 flex justify-between items-center group hover:bg-blue-50/50 px-2 rounded-md transition-all `}>
                  <div className={`flex gap-3 items-start `}>
                    {subTab === 'active' ? <Clock size={16} className="text-orange-500 mt-1" /> : <CheckCircle size={16} className="text-green-500 mt-1" />}
                    <div className="flex flex-col">
                      <span className="text-[12px] font-black text-gray-700">{task.title}</span>
                      <p className="text-[11px] text-gray-500 leading-tight">{task.message}</p>
                      <div className={`flex gap-2 mt-1 items-center `}>
                         <span className="bg-blue-100 text-[#005495] text-[9px] px-2 py-0.5 rounded font-bold">{task.app_name}</span>
                         <span className="text-[9px] text-gray-400 font-bold">{new Date(task.created_at).toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                  {subTab === 'active' && (
                    <button 
                      onClick={() => handleAction(task.link)}
                      className={`flex items-center gap-1 bg-[#005495] text-white px-3 py-1.5 rounded text-[10px] font-bold hover:bg-blue-700 transition-all opacity-0 group-hover:opacity-100 shadow-md `}
                    >
                      {t('dashboard.take_action')} <ArrowRight size={12} className={i18n.language === 'ar' ? 'rotate-180' : ''} />
                    </button>
                  )}
                </div>
              ))
            )}
            <PaginationControls />
          </div>
        )}
      </div>
    </div>
  );
};

// --- المكونات الحالية (BI) ---
const MaxeamBI = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar' || i18n.language === 'ur';
  const dashboardRef = useRef(null);
  const containerRef = useRef(null);
  const [refreshKey, setRefreshKey] = React.useState(0);

  const handleRefresh = () => { setRefreshKey(prev => prev + 1); };

  const toggleFullScreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen().catch(err => {
        alert(`Error: ${err.message}`);
      });
    } else {
      document.exitFullscreen();
    }
  };

  useEffect(() => {
    if (dashboardRef.current) {
      dashboardRef.current.innerHTML = "";
      const SUPERSET_DOMAIN = process.env.REACT_APP_SUPERSET_DOMAIN || "https://superset.maqaree.com";
      const DASHBOARD_ID = process.env.REACT_APP_SUPERSET_DASHBOARD_ID || "54904224-ddc7-4414-8ab1-ed910a27445e";
      
      embedDashboard({
        id: DASHBOARD_ID, 
        supersetDomain: SUPERSET_DOMAIN,
        mountPoint: dashboardRef.current,
        fetchGuestToken: async () => {
          const response = await fetch("/api/bi/guest-token");
          const data = await response.json();
          return data.token;
        },
        dashboardUiConfig: {
          hideTitle: true, 
          colorScheme: 'light', 
          hideTab: true, 
          hideChartControls: true,
          filters: { visible: false, expanded: false }
        },
      }).then(() => {
        const container = dashboardRef.current;
        if (container && container.children[0]) {
          const iframe = container.children[0];
          iframe.style.width = "100%";
          iframe.style.height = "100%";
          iframe.style.border = "none";
        }
      });
    }
  }, [refreshKey]);

  return (
    <div 
      ref={containerRef} 
      className="bg-white border border-gray-300 rounded shadow-sm mt-2 overflow-hidden flex flex-col transition-all"
      style={{ height: 'calc(100vh - 120px)' }}
    >
      <div className="bg-[#5c85b5] text-white px-3 py-1 flex justify-between items-center text-[11px] font-bold shrink-0">
        <div className={`flex items-center gap-2 `}>
          <BarChart2 size={14} />
          <span>{t('dashboard.advanced_analytics')}</span>
        </div>
        <div className={`flex items-center gap-3 `}>
            <RefreshCw size={12} className={`cursor-pointer hover:rotate-180 transition-transform duration-500 ${isRTL ? 'rotate-180' : ''}`} onClick={handleRefresh} />
            <Maximize2 size={12} className={`cursor-pointer hover:scale-125 transition-transform ${isRTL ? 'rotate-90' : ''}`} onClick={toggleFullScreen} />
        </div>
      </div>

      <div ref={dashboardRef} className="flex-1 w-full bg-white flex items-center justify-center overflow-hidden" />
      
      <style>{`
        iframe { background-color: white !important; height: 100% !important; width: 100% !important; border: none !important; ${isRTL ? 'direction: rtl;' : ''}} 
        :fullscreen { padding: 0 !important; background: white !important; }
        :fullscreen .shrink-0 { display: flex !important; }
      `}</style>
    </div>
  );
};

// الـ KPI Gauge مترجم
const KpiGauge = ({ title, actual, target, color }) => {
  const { t } = useTranslation();
  return (
    <div className="bg-white border border-gray-300 rounded shadow-sm overflow-hidden flex flex-col">
      <div className="bg-[#5c85b5] text-white px-3 py-1 flex justify-between items-center">
        <span className="text-[11px] font-bold">{t('dashboard.kpi_graph')}</span>
        <RefreshCw size={12} />
      </div>
      <div className="p-4 flex flex-col items-center">
        <p className="text-[11px] font-bold text-[#005495] mb-4 text-center h-8 leading-tight">{title}</p>
        <div className="relative w-40 h-24 flex justify-center overflow-hidden">
          <div className="w-40 h-40 rounded-full border-[15px] border-gray-100 border-b-transparent rotate-[-45deg]"></div>
          <div 
            className="absolute top-0 w-40 h-40 rounded-full border-[15px] border-b-transparent transition-all duration-1000 rotate-[45deg]"
            style={{ borderColor: color, transform: `rotate(${(Math.min(actual / target, 1) * 180) - 45}deg)` }}
          ></div>
          <div className="absolute bottom-2 text-2xl font-black">{actual}%</div>
        </div>
        <div className="w-full mt-4 flex justify-between text-[11px] border-t pt-2">
            <span>{t('dashboard.actual')}: <b className="text-sm">{actual}</b></span>
            <span>{t('dashboard.target')}: <b className="text-sm">{target}</b></span>
        </div>
      </div>
    </div>
  );
};

// --- المكون الرئيسي ---
export default function Dashboard() {
  const { t, i18n } = useTranslation();
  const [activeTab, setActiveTab] = useState('BI');

  return (
    <div className="h-full min-h-screen flex flex-col bg-[#f0f2f5]">
      <div className="bg-[#5c85b5] h-9 flex items-center px-2 text-white text-[11px]">
        <button 
          onClick={() => setActiveTab('BI')}
          className={`${activeTab === 'BI' ? 'bg-white text-[#005495] font-bold' : 'opacity-70 font-medium'} px-4 py-2 rounded-t-md cursor-pointer transition-all`}
        >
          {t('dashboard.tab_bi')}
        </button>
        <button 
          onClick={() => setActiveTab('INBOX')}
          className={`${activeTab === 'INBOX' ? 'bg-white text-[#005495] font-bold' : 'opacity-70 font-medium'} px-4 py-2 rounded-t-md cursor-pointer transition-all ${i18n.language === 'ar' || i18n.language === 'ur' ? 'mr-1' : 'ml-1'} flex items-center gap-2`}
        >
          <Inbox size={12} />
          <span>{t('dashboard.tab_inbox')}</span>
        </button>
      </div>

      <div className="p-6 overflow-y-auto">
        {activeTab === 'BI' ? (
          <MaxeamBI />
        ) : (
          <WorkflowInbox />
        )}
      </div>
    </div>
  );
}